import { useTheme } from '@mui/material/styles';
import { FC } from 'react';
import { Navigate } from 'react-router';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useAppSelector } from 'src/app/reduxCustomHooks';
import { SignIn } from 'src/components/organisms/auth/SignIn';
import LoginTemplate from 'src/components/templates/LoginTemplate';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
import { useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';

const Login: FC = () => {
  const token = useAppSelector((state) => state.auth.token);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  if (token) return <Navigate to="/" />;

  const welcomeMessage = (
    <Stack spacing={3} alignItems="center" sx={{ 
      width: '100%', 
      maxWidth: '400px',
      ml: { xs: 0, md: 'auto' }  // Reset margin-left on mobile, keep auto for desktop
    }}>
      <img
        width={!isMd ? 120 : 260}
        src={
          process.env.PUBLIC_URL +
          (theme.palette.mode === 'light'
            ? '/icons/svg/logo-blue.svg'
            : '/icons/png/logo.png')
        }
        alt="Anonify Logo"
        style={{ marginBottom: 8 }}
      />
      <Box
        sx={{
          background: (theme) => theme.palette.customGrey.main,
          color: 'text.primary',
          p: 3,
          borderRadius: 2,
          boxShadow: 2,
          width: '100%',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <InfoIcon sx={{ fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('login.welcome.title', 'Platformupdate')}
          </Typography>
        </Stack>
        <Typography variant="body1" sx={{ mb: 1.5 }}>
          {t(
            'login.welcome.message',
            'We hebben Anonify bijgewerkt en nieuwe functies toegevoegd.'
          )}
        </Typography>
        <Box
          component="ul"
          sx={{
            m: 0,
            mb: 2,
            pl: 2.5,
            textAlign: 'left',
            '& li': { mb: 0.75 },
            '& li:last-child': { mb: 0 },
          }}
        >
          <Typography component="li" variant="body2">
            {t(
              'login.welcome.feature1',
              'Verbeterd woordenboek voor eigen termen en uitzonderingen'
            )}
          </Typography>
          <Typography component="li" variant="body2">
            {t(
              'login.welcome.featureThorough',
              "Kies per werkruimte bij 'Herkenning' tussen Standaard en Grondig"
            )}
          </Typography>
          <Typography component="li" variant="body2">
            {t(
              'login.welcome.featureNotSure',
              "'Niet zeker'-markeringen in de editor tonen slecht leesbare tekst die je zelf controleert"
            )}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {t(
            'login.welcome.feedback',
            'We staan altijd open voor feedback en verbeteringen. Laat het ons vooral weten.'
          )}
        </Typography>
        <Button
          component="a"
          href="mailto:info@anonify.nl?subject=Feedback%20Anonify"
          variant="outlined"
          size="small"
          sx={{ mb: 1.5, textTransform: 'none' }}
        >
          {t('login.welcome.feedbackCta', 'Stuur feedback')}
        </Button>
        <Typography variant="body2">
          {t(
            'login.welcome.contact',
            'Mail naar info@anonify.nl of bel ons op +31 6 22731698.'
          )}
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <LoginTemplate>
      <Stack
        direction="row"
        justifyContent="space-evenly"
        spacing={4}
        sx={{
          // Mobile: stretch instead of centring so a tall form is not clipped at the top.
          alignItems: { xs: 'stretch', md: 'center' },
          width: { xs: '100%', md: 'auto' },
          // Mobile: at least full height, but grow with the content (no fixed height).
          minHeight: { xs: '100%', md: 'auto' },
        }}
      >
        <Box sx={{ 
          display: { xs: 'none', md: 'inline-flex' },
          maxWidth: '400px',
        }}>
          {welcomeMessage}
        </Box>
        <Paper
          elevation={0}
          sx={{
            minWidth: { xs: '100%', md: '400px', lg: '450px' },
            minHeight: { xs: '100%', md: 'auto' },
            borderRadius: {
              xs: 0,
              md: 2,
            },
            border: (theme) => ({
              xs: 'none',
              md: `1px solid ${theme.palette.hairline}`,
            }),
            textAlign: 'center',
            px: { xs: 4, sm: 8, md: 4, lg: 5 },
            pt: { xs: 6, md: 4, lg: 5 },
            pb: { xs: 3, md: 4, lg: 5 },
          }}
        >
          <Box
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              mb: 4,
              width: '100%',
            }}
          >
            {welcomeMessage}
          </Box>
          <SignIn />
        </Paper>
      </Stack>
    </LoginTemplate>
  );
};

export default Login;
